import React, { useState, useMemo, useEffect, useCallback } from "react";
import injectSheet from "react-jss";
import PropTypes from "prop-types";
import { default as doFormat } from "date-fns/format";
import seLocale from "date-fns/locale/sv";
import isValid from "date-fns/is_valid";
import classNames from "classnames";

import { DATE_YYYY_MM_DD } from "constants/regexp";
import FormInput from "customization/ui/FormInput/FormInput";
import CalendarView from "../CalendarView/CalendarView";
import IconDate from "components/CalenderIcon/CalenderIcon";
// import { FormInput, IconDate, CalendarView } from '../../../';
import { SESSION_CAM_EXCLUDE } from "constants/sessioncam";

const styles = ({ theme }) => ({
  root: {
    position: "relative"
  },
  calendar: {
    maxWidth: "90vw",
    width: 400,
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: theme.colors.white,
    fontFamily: theme.fonts.serif,
    padding: theme.padding[2],
    borderRadius: theme.borderRadius.default,
    overflow: "hidden",
    boxShadow: theme.shadows.md,
    zIndex: theme.zIndex["40"],
    opacity: 1,
    transition: "all 0.1s ease-out"
  },
  actionBar: {
    display: "flex",
    justifyContent: "space-between"
  },
  actionButton: {
    borderRadius: theme.borderRadius.default,
    padding: theme.padding[1],
    fontFamily: theme.fonts.serif,
    border: "none",
    color: theme.colors.primary2,
    fontWeight: theme.fontWeights.semibold,
    backgroundColor: theme.colors.white,
    fontSize: theme.textSizes.base,
    transition: "all 0.1s ease-in-out",
    "&:focus, &:not([disabled]):hover": {
      backgroundColor: theme.colors.senary4,
      outline: "none",
      cursor: "pointer"
    },
    "&:disabled": {
      color: theme.colors.senary3
    }
  },
  hidden: {
    opacity: 0,
    transform: "translateY(-10px)"
  }
});

const wrapInFakeEvent = (value, name) => ({
  target: {
    value,
    name
  },
  preventDefault: () => {
    console.warn(
      "This is a fake event that mimics the structure of the React SyntheticEvent but it does not implement its methods."
    );
  },
  stopPropagation: () => {
    console.warn(
      "This is a fake event that mimics the structure of the React SyntheticEvent but it does not implement its methods."
    );
  }
});

const FormInputDate = ({
  classes,
  id,
  value,
  format,
  invalid,
  placeholder,
  onChange,
  autoComplete,
  calendarProps,
  actionClearText,
  actionTodayText,
  actionCloseText,
  showActionClearButton,
  showActionTodayButton,
  showActionCloseButton,
  showActionBar,
  ...rest
}) => {
  let calendarRef = React.createRef();
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);

  const date = useMemo(
    () =>
      DATE_YYYY_MM_DD.test(value) && isValid(new Date(value))
        ? new Date(value)
        : null,
    [value]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  const handleClickOutside = useCallback(
    e => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        handleClose();
      }
    },
    [calendarRef, handleClose]
  );

  const handleKeydown = useCallback(e => {
    if (e.key.toLowerCase() === "escape") {
      setIsCalendarVisible(false);
    }
  }, []);

  const handleCalendarChange = useCallback(
    (value, keepOpen) => {
      onChange(
        wrapInFakeEvent(
          value ? doFormat(value, format, { locale: seLocale }) : ""
        )
      );
      setIsCalendarVisible(!!keepOpen);
    },
    [format, onChange]
  );

  const handleClose = useCallback(() => {
    setIsCalendarVisible(false);
  }, []);

  const openCalendarView = useCallback(() => {
    setIsCalendarVisible(true);
  }, []);

  const handleTodayClicked = useCallback(() => {
    onChange(
      wrapInFakeEvent(doFormat(new Date(), format, { locale: seLocale }))
    );
  }, [format, onChange]);

  const handleClearClicked = useCallback(() => {
    onChange(wrapInFakeEvent(""));
  }, [onChange]);
  return (
    <div className={classes.root}>
      <FormInput
        {...rest}
        id={id}
        invalid={invalid}
        placeholder={placeholder}
        autoComplete={autoComplete}
        suffix={<IconDate height={16} width={16} />}
        onChange={onChange}
        onFocus={openCalendarView}
        onClick={openCalendarView}
        value={value}
      />
      <div
        className={classNames(classes.calendar, {
          [classes.hidden]: !isCalendarVisible,
          [SESSION_CAM_EXCLUDE]: rest.sessionCamExclude
        })}
        ref={calendarRef}
      >
        {isCalendarVisible ? (
          <>
            <CalendarView
              value={date}
              onChange={handleCalendarChange}
              {...calendarProps}
            />
            {showActionBar && (
              <div className={classes.actionBar}>
                {showActionTodayButton && (
                  <button
                    type="button"
                    className={classes.actionButton}
                    onClick={handleTodayClicked}
                  >
                    {actionTodayText}
                  </button>
                )}
                {showActionClearButton && (
                  <button
                    type="button"
                    className={classes.actionButton}
                    onClick={handleClearClicked}
                  >
                    {actionClearText}
                  </button>
                )}
                {showActionCloseButton && (
                  <button
                    type="button"
                    className={classes.actionButton}
                    onClick={handleClose}
                  >
                    {actionCloseText}
                  </button>
                )}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

FormInputDate.defaultProps = {
  autoComplete: "off",
  actionClearText: "Rensa",
  actionTodayText: "Idag",
  actionCloseText: "Stäng",
  showActionBar: true,
  showActionClearButton: true,
  showActionTodayButton: true,
  showActionCloseButton: true,
  format: "YYYY-MM-DD",
  calendarProps: {
    locale: "sv",
    maxDetail: "month",
    minDetail: "year",
    showWeekNumbers: true
  }
};

FormInputDate.propTypes = {
  /** We're assuming we only need to handle string values, like "YYYY-MM-DD" */
  value: PropTypes.string,
  /** sets the styling to invalid in the underlying FormInput component */
  invalid: PropTypes.bool,
  /** sets the placeholder in the input*/
  placeholder: PropTypes.string,
  /** How to format the date in the input */
  format: PropTypes.string,
  onChange: PropTypes.func,
  /** Enable or disable the native html autocomplete */
  autoComplete: PropTypes.string,
  /** Text used in calendar view action bar */
  actionClearText: PropTypes.string,
  /** Text used in calendar view action bar */
  actionTodayText: PropTypes.string,
  /** Text used in calendar view action bar */
  actionCloseText: PropTypes.string,
  /** See the `CalendarView` component for a list of suppoerter props */
  calendarProps: PropTypes.shape(),
  /** Shows or hides action bar Clear button */
  showActionClearButton: PropTypes.bool,
  /** Shows or hides action bar Today button */
  showActionTodayButton: PropTypes.bool,
  /** Shows or hides action bar Close button */
  showActionCloseButton: PropTypes.bool,
  /** Shows or hides action bar */
  showActionBar: PropTypes.bool,
  /** Indicates if this field has been excluded from recording by sessioncam */
  sessionCamExclude: PropTypes.bool
};

export default injectSheet(styles, { inject: ["classes"] })(FormInputDate);
